import {
	type PageQuery,
	type PagedResult,
	matchesSearch,
	resolveSample,
} from "@/core/data/sampleDataSource";
import { ACTIVE_PARTNER_CATALOG } from "@/features/consignment-vendor-product/data/productCatalogFixtures";
import { loadSampleCatalog } from "@/features/consignment-vendor-product/data/productDataSource";
import { SAMPLE_ACTIVE_PARTNER } from "@/features/consignment-vendor/data/samplePartnerFixtures";

export const STOCK_OUT_TYPE = {
	TakenByVendor: "taken_by_vendor",
	Destroyed: "destroyed",
} as const;

export type StockOutType = (typeof STOCK_OUT_TYPE)[keyof typeof STOCK_OUT_TYPE];

export const STOCK_OUT_TAB = {
	All: "semua",
	TakenByVendor: STOCK_OUT_TYPE.TakenByVendor,
	Destroyed: STOCK_OUT_TYPE.Destroyed,
} as const;

export type StockOutTab = (typeof STOCK_OUT_TAB)[keyof typeof STOCK_OUT_TAB];

export type StockOutLine = {
	productCode: string;
	productName: string;
	quantity: number;
};

export type StockOut = {
	stockOutId: string;
	stockOutNumber: string;
	storeId: string;
	storeName: string;
	stockOutDate: string;
	stockOutType: StockOutType;
	stockOutReason: string;
	totalQuantity: number;
	lines: StockOutLine[];
};

export type StockOutListResult = PagedResult<StockOut> & {
	tabCounts: Record<StockOutTab, number>;
	summary: {
		eventCount: number;
		totalQuantity: number;
		takenByVendorCount: number;
		destroyedCount: number;
	};
};

export type StockOutDataSource = {
	listStockOuts: (
		query: PageQuery<{ stockOutTab: StockOutTab }>,
	) => Promise<StockOutListResult>;
	loadStockOut: (stockOutId: string) => Promise<StockOut>;
};

type StockOutRecord = Omit<
	StockOut,
	"storeName" | "totalQuantity" | "lines"
> & {
	lines: { productCode: string; quantity: number }[];
};

const STOCK_OUT_NOT_FOUND_MESSAGE = "Stock-out tidak ditemukan.";

// Sample numbers are fictional.
export const SAMPLE_STOCK_OUTS: StockOutRecord[] = [
	{
		stockOutId: "019a3f1c-7e22-7c43-8e01-4a7d3b91c001",
		stockOutNumber: "CSO/251007/00000312",
		storeId: "store-kokas",
		stockOutDate: "2026-10-07",
		stockOutType: STOCK_OUT_TYPE.TakenByVendor,
		stockOutReason:
			"Produk ditarik OPS: kemasan penyok pada 3 pcs, diambil mitra saat kunjungan.",
		lines: [{ productCode: "899743540208", quantity: 3 }],
	},
	{
		stockOutId: "019a3f1c-7e22-7c43-8e02-4a7d3b91c002",
		stockOutNumber: "CSO/251005/00000298",
		storeId: "store-scbd",
		stockOutDate: "2026-10-05",
		stockOutType: STOCK_OUT_TYPE.Destroyed,
		stockOutReason:
			"Melewati tanggal kedaluwarsa, dimusnahkan toko dengan berita acara.",
		lines: [
			{ productCode: "699170286139", quantity: 2 },
			{ productCode: "151462779163", quantity: 4 },
		],
	},
	{
		stockOutId: "019a3f1c-7e22-7c43-8e03-4a7d3b91c003",
		stockOutNumber: "CSO/250930/00000250",
		storeId: "store-kokas",
		stockOutDate: "2026-09-30",
		stockOutType: STOCK_OUT_TYPE.TakenByVendor,
		stockOutReason:
			"Produk dinonaktifkan di toko ini, sisa stok dikembalikan ke mitra.",
		lines: [
			{ productCode: "165782751746", quantity: 5 },
			{ productCode: "134759234543", quantity: 6 },
		],
	},
	{
		stockOutId: "019a3f1c-7e22-7c43-8e04-4a7d3b91c004",
		stockOutNumber: "CSO/250926/00000221",
		storeId: "store-scbd",
		stockOutDate: "2026-09-26",
		stockOutType: STOCK_OUT_TYPE.Destroyed,
		stockOutReason: "Rusak saat penataan rak, dimusnahkan toko.",
		lines: [{ productCode: "574101949307", quantity: 2 }],
	},
];

const toStockOut = (stockOutRecord: StockOutRecord): StockOut => {
	const lines = stockOutRecord.lines.map((line) => ({
		...line,
		productName:
			ACTIVE_PARTNER_CATALOG.find(
				(catalogProduct) => catalogProduct.productCode === line.productCode,
			)?.productName ?? line.productCode,
	}));
	return {
		...stockOutRecord,
		storeName:
			SAMPLE_ACTIVE_PARTNER.stores.find(
				(store) => store.storeId === stockOutRecord.storeId,
			)?.storeName ?? "",
		totalQuantity: lines.reduce((total, line) => total + line.quantity, 0),
		lines,
	};
};

const loadPartnerStockOuts = () =>
	loadSampleCatalog().isNewPartnerAccount
		? []
		: SAMPLE_STOCK_OUTS.map(toStockOut);

const matchesTab = (stockOut: StockOut, stockOutTab: StockOutTab) =>
	stockOutTab === STOCK_OUT_TAB.All || stockOut.stockOutType === stockOutTab;

const sampleStockOutDataSource: StockOutDataSource = {
	listStockOuts: (query) => {
		const stockOutTab = query.filters?.stockOutTab ?? STOCK_OUT_TAB.All;
		const allStockOuts = loadPartnerStockOuts();
		const matchingStockOuts = allStockOuts.filter(
			(stockOut) =>
				matchesTab(stockOut, stockOutTab) &&
				matchesSearch(
					query.search,
					`${stockOut.stockOutNumber} ${stockOut.storeName}`,
				),
		);
		const pageStart = (query.page - 1) * query.pageSize;
		const countByType = (stockOutType: StockOutType) =>
			allStockOuts.filter((stockOut) => stockOut.stockOutType === stockOutType)
				.length;
		const result: StockOutListResult = {
			items: matchingStockOuts.slice(pageStart, pageStart + query.pageSize),
			total: matchingStockOuts.length,
			page: query.page,
			pageSize: query.pageSize,
			tabCounts: {
				[STOCK_OUT_TAB.All]: allStockOuts.length,
				[STOCK_OUT_TAB.TakenByVendor]: countByType(
					STOCK_OUT_TYPE.TakenByVendor,
				),
				[STOCK_OUT_TAB.Destroyed]: countByType(STOCK_OUT_TYPE.Destroyed),
			},
			summary: {
				eventCount: allStockOuts.length,
				totalQuantity: allStockOuts.reduce(
					(total, stockOut) => total + stockOut.totalQuantity,
					0,
				),
				takenByVendorCount: countByType(STOCK_OUT_TYPE.TakenByVendor),
				destroyedCount: countByType(STOCK_OUT_TYPE.Destroyed),
			},
		};
		return resolveSample(result, { ...result, items: [], total: 0 });
	},
	loadStockOut: async (stockOutId) => {
		const stockOut = loadPartnerStockOuts().find(
			(candidate) => candidate.stockOutId === stockOutId,
		);
		if (!stockOut) throw new Error(STOCK_OUT_NOT_FOUND_MESSAGE);
		return resolveSample(stockOut, stockOut);
	},
};

export const stockOutDataSource = sampleStockOutDataSource;
